'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/context/LanguageContext';
import { languageConfig } from '@/lib/i18n-config';

// Language region mapping for SEO
const languageRegionMap = {
  de: 'CH', // German (Switzerland)
  en: 'CH', // English (Switzerland)
  fr: 'CH', // French (Switzerland)
  es: 'ES'  // Spanish (Spain)
};

// Language-specific property type naming for enhanced SEO with more keyword variations
const propertyTypeByLanguage = {
  de: 'Ferienhaus Engelberg', // German: Vacation Home Engelberg
  en: 'Vacation Rental Engelberg',
  fr: 'Location de Vacances Engelberg',
  es: 'Casa de Vacaciones Engelberg'
};

// Language-specific property descriptions with targeted keywords and improved SEO
const propertyDescriptionByLanguage = {
  de: 'Gemütliches Ferienhaus in Engelberg mit Selbstverpflegung und atemberaubendem Blick auf Titlis und Hahnen. Zwei separate Wohneinheiten mit voll ausgestatteter Küche und privater Terrasse, ideal für Familien und längere Aufenthalte. Nur 10 Minuten vom Skigebiet entfernt. Das perfekte Zuhause für Ihren Schweizer Alpenurlaub.',
  en: 'Self-catering vacation rental in Engelberg with breathtaking views of Titlis and Hahnen. Two separate units with fully equipped kitchens and private terrace, ideal for families and extended stays. Just 10 minutes from the ski area. Your perfect home away from home in the Swiss Alps.',
  fr: 'Location de vacances à Engelberg avec cuisine équipée et vue imprenable sur Titlis et Hahnen. Deux unités séparées avec terrasse privée, idéal pour les familles et les longs séjours. À seulement 10 minutes du domaine skiable. Votre maison parfaite dans les Alpes suisses.',
  es: 'Casa de vacaciones con cocina equipada en Engelberg con impresionantes vistas de Titlis y Hahnen. Dos unidades separadas con terraza privada, ideal para familias y estancias prolongadas. A solo 10 minutos de la zona de esquí. Su hogar perfecto en los Alpes suizos.'
};

type SupportedLanguage = 'de' | 'en' | 'fr' | 'es';

// Types for reviews
interface Review {
  author: string;
  reviewRating: number;
  reviewBody: string;
  datePublished?: string; // ISO date string
}

// Types for FAQ items
interface FAQItem {
  question: string;
  answer: string;
}

// Type for article dates
interface ArticleDates {
  datePublished: string; // ISO date string
  dateModified?: string; // ISO date string
}

// Type for breadcrumb items
interface BreadcrumbItem {
  name: string;
  item: string;
}

// Type for amenity features
interface AmenityFeature {
  name: string;
  value?: boolean | string;
}

interface SchemaMarkupProps {
  baseUrl?: string;
  pageType?: 'VacationRental' | 'Article' | 'WebPage' | 'FAQPage';
  pageTitle?: string;
  pageDescription?: string;
  address?: {
    street: string;
    locality: string;
    postalCode: string;
    country: string;
  };
  phone?: string;
  geo?: {
    latitude: number;
    longitude: number;
  };
  priceRange?: string;
  // New props for enhanced schemas
  reviews?: Review[];
  faqItems?: FAQItem[];

  articleDates?: ArticleDates;
  imageUrl?: string;
  author?: string;
  numberOfRooms?: number;
  numberOfBedrooms?: number;
  numberOfBathrooms?: number;
  maxOccupancy?: number;
  amenityFeature?: AmenityFeature[]; // Vacation rental specific amenities
  minimumStayLength?: string; // Minimum stay length for vacation rentals
  petsAllowed?: boolean; // Whether pets are allowed
  floorSize?: { // Floor size of the property
    value: number;
    unitCode: string; // e.g., "MTK" for square meters
  };
  yearBuilt?: string; // Year the property was built
  acceptsReservations?: boolean; // Whether the property accepts reservations
  additionalProperty?: Array<{ name: string, value: string }>; // Additional property attributes
  breadcrumbs?: BreadcrumbItem[]; // Breadcrumb data for BreadcrumbList schema
  identifier?: {
    type: string;
    value: string;
  }; // Identifier for the property
}

/**
 * Enhanced SchemaMarkup component for Vacation Rental SEO optimization
 * 
 * Dynamically generates:
 * 1. Hreflang tags for language variants with self-reference and x-default
 * 2. JSON-LD structured data for rich results based on page type
 * 3. Open Graph tags for social media sharing
 * 
 * @param baseUrl Base URL for the website (defaults to tannenboden.com)
 * @param pageType Type of schema to generate (defaults to VacationRental)
 * @param pageTitle Optional page title for schema
 * @param pageDescription Optional page description for schema
 * @param address Optional address object for VacationRental schema
 * @param phone Optional phone number for VacationRental schema
 * @param geo Optional geographic coordinates
 * @param priceRange Optional price range string
 * @param reviews Optional array of review objects for VacationRental schema
 * @param faqItems Optional array of FAQ items for FAQPage schema
 * @param articleDates Optional object with datePublished and dateModified for Article schema
 * @param imageUrl Optional image URL for Article schema
 * @param author Optional author name for Article schema
 * @param numberOfRooms Total number of rooms in the property
 * @param numberOfBedrooms Number of bedrooms in the property
 * @param numberOfBathrooms Number of bathrooms in the property
 * @param maxOccupancy Maximum number of guests the property can accommodate
 * @param amenityFeature Array of amenity features specifically for vacation rentals
 * @param minimumStayLength Minimum stay length (e.g., "2 nights")
 * @param petsAllowed Whether pets are allowed in the property
 * @param floorSize Floor size of the property with value and unitCode
 * @param yearBuilt Year the property was built
 * @param acceptsReservations Whether the property accepts reservations
 * @param additionalProperty Additional property attributes for the vacation rental
 */
const SchemaMarkup: React.FC<SchemaMarkupProps> = ({
  baseUrl = 'https://tannenboden.com',
  pageType = 'VacationRental',
  pageTitle,
  pageDescription,
  address = {
    street: process.env.NEXT_PUBLIC_CHALET_STREET || 'Sattelboden 1',
    locality: process.env.NEXT_PUBLIC_CHALET_LOCALITY || 'Engelberg',
    postalCode: process.env.NEXT_PUBLIC_CHALET_POSTAL || '6390',
    country: process.env.NEXT_PUBLIC_CHALET_COUNTRY || 'CH'
  },
  phone = process.env.NEXT_PUBLIC_CHALET_PHONE || '+41787460447',
  geo = {
    latitude: parseFloat(process.env.NEXT_PUBLIC_CHALET_LATITUDE || '46.82269399723277'),
    longitude: parseFloat(process.env.NEXT_PUBLIC_CHALET_LONGITUDE || '8.383846396985424')
  },
  identifier = {
    type: "PropertyID",
    value: "tannenboden-chalet-engelberg"
  },
  priceRange = process.env.NEXT_PUBLIC_CHALET_PRICE_RANGE || '150-400 CHF',
  // New props with default values
  reviews = [],
  faqItems = [],
  articleDates,
  imageUrl,
  author,
  // Property details for vacation rental
  numberOfRooms = 6, // Total rooms across both units
  numberOfBedrooms = 3, // 2 upstairs + 1 downstairs
  numberOfBathrooms = 2, // 1 upstairs + 1 downstairs
  maxOccupancy = 8, // Maximum number of guests
  // Vacation rental specific properties
  amenityFeature = [
    { name: "Fully equipped kitchen" },
    { name: "Free WiFi" },
    { name: "Private terrace" },
    { name: "Self check-in available" },
    { name: "Washing machine" },
    { name: "Free parking" }
  ],
  minimumStayLength = "2 nights",
  petsAllowed = false,
  floorSize = {
    value: 120,
    unitCode: "MTK" // Square meters
  },
  yearBuilt = "1960",
  acceptsReservations = true,
  additionalProperty = [
    {
      name: "selfCatering",
      value: "true"
    },
    {
      name: "vacationRentalCategory",
      value: "chalet"
    }
  ],
  breadcrumbs = [] // Breadcrumb navigation items
}) => {
  const pathname = usePathname();
  const { language } = useLanguage();
  const [currentPath, setCurrentPath] = useState<string>('');
  const [canonicalUrl, setCanonicalUrl] = useState<string>('');

  // Extract the path without the language prefix
  useEffect(() => {
    if (!pathname) return;

    const pathParts = pathname.split('/');
    // Skip the first empty part and the language code
    const pathWithoutLanguage = pathParts.length > 2
      ? `/${pathParts.slice(2).join('/')}`
      : '/';

    setCurrentPath(pathWithoutLanguage);

    // Set canonical URL - if we're on the homepage, don't include trailing slash
    const canonicalPath = pathWithoutLanguage === '/' ? '' : pathWithoutLanguage;
    setCanonicalUrl(`${baseUrl}/${language}${canonicalPath}`);
  }, [pathname, language, baseUrl]);

  /**
   * Generate hreflang tags for all supported languages using SEO-friendly URL structure
   * - Includes self-reference for current language
   * - Adds x-default pointing to German (most common visitor language)
   * - Uses language-specific prefixes for better SEO
   */
  const generateHreflangTags = () => {
    const supportedLanguages: SupportedLanguage[] = ['de', 'en', 'fr', 'es'];
    const tags = [];

    // Map the current path to SEO-friendly paths for each language
    const seoPathMap: Record<string, string> = {};

    // Extract the page slug (last part of the path)
    const pathSegments = currentPath.split('/').filter(Boolean);
    const pageSlug = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';

    // Generate SEO-friendly URLs for each language
    supportedLanguages.forEach(lang => {
      const config = languageConfig[lang as keyof typeof languageConfig];

      if (lang === 'en') {
        // English uses /engelberg-accommodation/page-slug
        seoPathMap[lang] = pageSlug ? `${config.prefix}/${pageSlug}` : config.prefix;
      } else {
        // Other languages use /lang/prefix/page-slug
        seoPathMap[lang] = pageSlug ? `${config.prefix}/${pageSlug}` : `/${lang}`;
      }
    });

    // Add x-default hreflang (points to German version as it's the primary market)
    tags.push(
      <link
        key="hreflang-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={`${baseUrl}${seoPathMap.de}`}
      />
    );

    // Add language-specific hreflang tags
    supportedLanguages.forEach(lang => {
      tags.push(
        <link
          key={`hreflang-${lang}`}
          rel="alternate"
          hrefLang={lang}
          href={`${baseUrl}${seoPathMap[lang]}`}
        />
      );
    });

    // Add canonical URL - always use German version as canonical regardless of current language
    // This ensures search engines have a single preferred URL for all content
    const canonicalPath = seoPathMap['de'];

    tags.push(
      <link
        key="canonical"
        rel="canonical"
        href={`${baseUrl}${canonicalPath}`}
      />
    );

    return tags;
  };

  /**
   * Generate Open Graph tags for social media sharing
   */
  const generateOpenGraphTags = () => {
    // Enhanced titles with targeted keywords for better SEO
    const defaultTitle = {
      'en': 'Tannenboden Vacation Apartment Engelberg | Authentic Mountain Experience in Swiss Alps',
      'de': 'Tannenboden Ferienwohnung Engelberg | Gemütliche Unterkunft mit Bergblick',
      'fr': 'Tannenboden Appartement de Vacances Engelberg | Expérience Authentique en Montagne dans les Alpes Suisses',
      'es': 'Tannenboden Apartamento de Vacaciones Engelberg | Experiencia Auténtica de Montaña en los Alpes Suizos'
    }[language as SupportedLanguage] || 'Tannenboden Ferienwohnung Engelberg';

    // Using the enhanced language-specific descriptions
    const defaultDescription = propertyDescriptionByLanguage[language as SupportedLanguage] || propertyDescriptionByLanguage.de;

    return [
      <meta key="og:type" property="og:type" content="website" />,
      <meta key="og:url" property="og:url" content={canonicalUrl} />,
      <meta key="og:title" property="og:title" content={pageTitle || defaultTitle} />,
      <meta key="og:description" property="og:description" content={pageDescription || defaultDescription} />,
      <meta key="og:image" property="og:image" content={imageUrl || `${baseUrl}/images/ferienwohnung-engelberg-apartment-living.webp`} />,
      <meta key="og:locale" property="og:locale" content={`${language}_${languageRegionMap[language as SupportedLanguage]}`} />
    ];
  };

  /**
   * Generate BreadcrumbList schema when breadcrumb data is provided
   */
  const generateBreadcrumbSchema = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    const breadcrumbData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((item: BreadcrumbItem, index: number) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': `${baseUrl}${item.item}`
      }))
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
    );
  };

  /**
   * Generate enhanced JSON-LD structured data based on page type
   */
  const generateStructuredData = () => {
    // Common data for all schema types
    const commonData = {
      '@context': 'https://schema.org',
      'name': pageTitle || 'Tannenboden Chalet Engelberg',
      'description': pageDescription || {
        'en': 'Authentic Swiss mountain accommodation in Engelberg with breathtaking views of Titlis and Hahnen, surrounded by pristine nature and traditional Alpine charm.',
        'de': 'Authentische Schweizer Bergunterkünfte in Engelberg mit atemberaubendem Blick auf Titlis und Hahnen, umgeben von unberührter Natur und traditionellem Alpencharme.',
        'fr': 'Hébergement authentique à la montagne suisse à Engelberg avec vue imprenable sur Titlis et Hahnen, entouré de nature préservée et de charme alpin traditionnel.',
        'es': 'Alojamiento auténtico de montaña suiza en Engelberg con impresionantes vistas de Titlis y Hahnen, rodeado de naturaleza prístina y encanto alpino tradicional.'
      }[language as SupportedLanguage] || '',
      'url': canonicalUrl,
      'inLanguage': language,
    };

    // Schema type-specific data
    let structuredData;

    switch (pageType) {
      case 'Article':
        // Use provided dates or fallback to current date
        const articleDatePublished = articleDates?.datePublished || new Date().toISOString();
        const articleDateModified = articleDates?.dateModified || articleDates?.datePublished || new Date().toISOString();

        structuredData = {
          ...commonData,
          '@type': 'Article',
          'headline': pageTitle || 'Tannenboden Chalet Engelberg',
          'image': imageUrl || `${baseUrl}/images/chalet-exterior.webp`,
          'datePublished': articleDatePublished,
          'dateModified': articleDateModified,
          'author': {
            '@type': 'Person',
            'name': author || 'Tannenboden Team'
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'Tannenboden Chalet',
            'logo': {
              '@type': 'ImageObject',
              'url': `${baseUrl}/icons/icon-72x72.webp`
            }
          }
        };
        break;

      case 'FAQPage':
        // Generate FAQ schema with provided items
        const faqEntities = faqItems && faqItems.length > 0
          ? faqItems.map(item => ({
            '@type': 'Question',
            'name': item.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': item.answer
            }
          }))
          : [];

        structuredData = {
          ...commonData,
          '@type': 'FAQPage',
          'mainEntity': faqEntities
        };
        break;

      case 'WebPage':
        structuredData = {
          ...commonData,
          '@type': 'WebPage',
          'speakable': {
            '@type': 'SpeakableSpecification',
            'cssSelector': ['h1', 'h2', '.main-content']
          },
          'primaryImageOfPage': {
            '@type': 'ImageObject',
            'url': `${baseUrl}/images/chalet-exterior.webp`
          }
        };
        break;

      case 'VacationRental':
      default:
        // Add reviews if provided
        const reviewsData = reviews && reviews.length > 0
          ? reviews.map(review => ({
            '@type': 'Review',
            'author': {
              '@type': 'Person',
              'name': review.author
            },
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': review.reviewRating,
              'bestRating': '5'
            },
            'reviewBody': review.reviewBody,
            'datePublished': review.datePublished || new Date().toISOString()
          }))
          : [];

        // Calculate aggregate rating if reviews are provided
        const aggregateRating = reviews && reviews.length > 0
          ? {
            '@type': 'AggregateRating',
            'ratingValue': (reviews.reduce((sum, review) => sum + review.reviewRating, 0) / reviews.length).toFixed(1),
            'reviewCount': reviews.length
          }
          : {
            '@type': 'Rating',
            'ratingValue': '4.8'
          };

        // Define structured data based on actual property characteristics
        structuredData = {
          ...commonData,
          '@type': 'VacationRental',
          '@id': `${baseUrl}/#vacation-rental`,
          'additionalType': [
            'https://schema.org/Apartment',
            'https://schema.org/VacationRental',
            'https://schema.org/House'
          ],
          'alternateName': propertyTypeByLanguage[language as SupportedLanguage] || propertyTypeByLanguage.de,
          'name': language === 'de' ? 'Ferienhaus Engelberg Tannenboden' : `Tannenboden ${propertyTypeByLanguage[language as SupportedLanguage] || 'Vacation Rental Engelberg'}`,
          'telephone': phone,
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': address.street,
            'addressLocality': address.locality,
            'addressRegion': 'Obwalden', // Adding required addressRegion field
            'postalCode': address.postalCode,
            'addressCountry': address.country
          },
          'geo': {
            '@type': 'GeoCoordinates',
            'latitude': geo.latitude,
            'longitude': geo.longitude
          },
          'priceRange': priceRange,
          // Enhanced amenities with specific details relevant to vacation rentals
          'amenityFeature': [
            // Convert provided amenities to schema format
            ...amenityFeature.map(amenity => ({
              '@type': 'LocationFeatureSpecification',
              'name': amenity.name,
              'value': amenity.value !== undefined ? amenity.value : true
            })),
            // Add these default amenities if not already specified
            { '@type': 'LocationFeatureSpecification', 'name': 'Ski Access', 'value': true },
            { '@type': 'LocationFeatureSpecification', 'name': 'Mountain Views', 'value': true },
            { '@type': 'LocationFeatureSpecification', 'name': 'Self-Catering', 'value': true },
            { '@type': 'LocationFeatureSpecification', 'name': 'Long-Stay Friendly', 'value': true }
          ],
          ...(reviews && reviews.length > 0 ? { 'review': reviewsData } : {}),
          ...(reviews && reviews.length > 0 ? { 'aggregateRating': aggregateRating } : { 'starRating': aggregateRating }),
          'availableLanguage': [
            { '@type': 'Language', 'name': 'German' },
            { '@type': 'Language', 'name': 'English' },
            { '@type': 'Language', 'name': 'French' },
            { '@type': 'Language', 'name': 'Spanish' }
          ],
          // Enhanced photo descriptions with SEO-friendly names
          'photo': [
            {
              '@type': 'ImageObject',
              'url': `${baseUrl}/images/ferienwohnung-engelberg-apartment-living.webp`,
              'name': 'Ferienwohnung Engelberg Wohnbereich mit Bergblick'
            },
            {
              '@type': 'ImageObject',
              'url': `${baseUrl}/images/ferienwohnung-engelberg-apartment-bedroom.webp`,
              'name': 'Ferienwohnung Engelberg Schlafzimmer'
            },
            {
              '@type': 'ImageObject',
              'url': `${baseUrl}/images/ferienwohnung-engelberg-exterior.webp`,
              'name': 'Ferienwohnung Engelberg Außenansicht'
            },
            {
              '@type': 'ImageObject',
              'url': `${baseUrl}/images/ferienwohnung-engelberg-titlis-view.webp`,
              'name': 'Aussicht auf Titlis von der Ferienwohnung Engelberg'
            },
            {
              '@type': 'ImageObject',
              'url': `${baseUrl}/images/attractions/exterior/view-titlis-winter.webp`,
              'name': 'Winteraussicht vom Tannenboden Ferienwohnung Engelberg'
            }
          ],
          // Check-in/check-out times
          'checkinTime': '16:00',
          'checkoutTime': '10:00',
          // New property details
          'numberOfRooms': numberOfRooms,
          'numberOfBedrooms': numberOfBedrooms,
          'numberOfBathrooms': numberOfBathrooms,
          'occupancy': {
            '@type': 'QuantitativeValue',
            'maxValue': maxOccupancy
          },
          // Add schema for each accommodation unit
          // Top-level image property (required by Google)
          'image': [
            `${baseUrl}/images/ferienwohnung-engelberg-apartment-living.webp`,
            `${baseUrl}/images/ferienwohnung-engelberg-apartment-bedroom.webp`,
            `${baseUrl}/images/ferienwohnung-engelberg-exterior.webp`,
            `${baseUrl}/images/ferienwohnung-engelberg-titlis-view.webp`,
            `${baseUrl}/images/ferienwohnung-engelberg-winter.webp`,
            `${baseUrl}/images/attractions/exterior/view-titlis-winter.webp`,
            `${baseUrl}/images/attractions/exterior/house-tannenboden-drone.webp`,
            `${baseUrl}/images/chalet-exterior.webp`
          ],
          // Add top-level identifier
          'identifier': {
            '@type': 'PropertyValue',
            'name': 'Tannenboden Chalet Engelberg',
            'value': 'tannenboden-chalet-engelberg'
          },
          'containsPlace': [
            {
              '@type': 'Accommodation',
              'name': 'Upstairs Apartment',
              'numberOfRooms': 4,
              'numberOfBedrooms': 2,
              'numberOfBathroomsTotal': 1,
              'identifier': {
                '@type': 'PropertyValue',
                'name': 'Upstairs Unit',
                'value': 'tannenboden-upstairs'
              },
              'occupancy': {
                '@type': 'QuantitativeValue',
                'maxValue': 5,
                'value': '5'
              },
              'bed': [
                {
                  '@type': 'BedDetails',
                  'numberOfBeds': 1,
                  'typeOfBed': 'Double Bed'
                },
                {
                  '@type': 'BedDetails',
                  'numberOfBeds': 3,
                  'typeOfBed': 'Single Bed'
                }
              ],
              'amenityFeature': [
                { '@type': 'LocationFeatureSpecification', 'name': 'Living Room', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Full Kitchen', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Mountain Views', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Heating', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Free WiFi', 'value': true }
              ]
            },
            {
              '@type': 'Accommodation',
              'name': 'Downstairs Apartment',
              'numberOfRooms': 2,
              'numberOfBedrooms': 1,
              'numberOfBathroomsTotal': 1,
              'identifier': {
                '@type': 'PropertyValue',
                'name': 'Downstairs Unit',
                'value': 'tannenboden-downstairs'
              },
              'occupancy': {
                '@type': 'QuantitativeValue',
                'maxValue': 3,
                'value': '3'
              },
              'bed': [
                {
                  '@type': 'BedDetails',
                  'numberOfBeds': 1,
                  'typeOfBed': 'Double Bed'
                },
                {
                  '@type': 'BedDetails',
                  'numberOfBeds': 1,
                  'typeOfBed': 'Single Bed'
                }
              ],
              'amenityFeature': [
                { '@type': 'LocationFeatureSpecification', 'name': 'Combined Bedroom-Living Area', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Kitchen', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Private Terrace', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Heating', 'value': true },
                { '@type': 'LocationFeatureSpecification', 'name': 'Free WiFi', 'value': true }
              ]
            }
          ],
          // Add reservation action for better search features
          'potentialAction': {
            '@type': 'ReserveAction',
            'target': {
              '@type': 'EntryPoint',
              'urlTemplate': `${baseUrl}/${language === 'en' ? 'engelberg-accommodation/booking/availability' : language === 'de' ? 'de/engelberg-ferienwohnung/buchung/verfugbarkeit' : language === 'fr' ? 'fr/hebergement-engelberg/reservation/disponibilite' : 'es/alojamiento-engelberg/reserva/disponibilidad'}`
            }
          },
          // Expanded keywords especially useful for German searches
          // Add vacation rental specific properties
          'minimumStayLength': minimumStayLength,
          'floorSize': {
            '@type': 'QuantitativeValue',
            'value': floorSize.value,
            'unitCode': floorSize.unitCode
          },
          'yearBuilt': yearBuilt,
          'acceptsReservations': acceptsReservations,
          // Add additional properties
          'additionalProperty': additionalProperty.map(prop => ({
            '@type': 'PropertyValue',
            'name': prop.name,
            'value': prop.value
          })),

          'keywords': language === 'de' ? [
            'Ferienhaus Engelberg',
            'Ferienhaus Engelberg mit Bergblick',
            'Ferienhaus Engelberg Titlis',
            'Ferienhaus Engelberg mit Selbstverpflegung',
            'Ferienhaus Engelberg für Familien',
            'Ferienhaus Engelberg Terrasse',
            'Ferienhaus Engelberg mieten',
            'Ferienhaus Engelberg Skigebiet',
            'Ferienhaus Engelberg Sattelboden',
            'Ferienunterkunft Engelberg mit Bergblick',
            'Ferienhaus Schweizer Alpen',
            'Selbstversorger-Unterkunft Engelberg',
            'Gemütliches Ferienhaus Engelberg',
            'Chalet Engelberg mit Küche',
            'Langzeitmiete Engelberg'
          ].join(',') : [
            'Vacation rental Engelberg',
            'Self-catering accommodation Engelberg',
            'Mountain view vacation home Engelberg',
            'Ski chalet Engelberg',
            'Family vacation rental Swiss Alps',
            'Engelberg rental with kitchen',
            'Swiss Alps self-catering chalet',
            'Titlis view vacation rental',
            'Holiday home Engelberg',
            'Extended stay accommodation Engelberg',
            'Home away from home Engelberg',
            'Vacation home with kitchen Engelberg'
          ].join(',')
        };
    }

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    );
  };

  // Only render on client side to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Return null during server-side rendering and initial client render
  if (!isMounted) {
    return null;
  }

  return (
    <>
      {/* Hreflang tags and canonical URL */}
      {generateHreflangTags()}

      {/* Open Graph tags */}
      {generateOpenGraphTags()}

      {/* Structured data */}
      {generateStructuredData()}

      {/* Breadcrumb schema if data is provided */}
      {generateBreadcrumbSchema()}
    </>
  );
};

export default SchemaMarkup;